
import usePosOrders from "../../../services/Pos/usePosOrders";
import PosKOTPrint from "./PosKOTPrint";
import POSPrint from "./PosPrint";

export default function CashPaymentModal({
  cart = [],
  total = 0,
  paidAmount = 0,
  discount = 0,
  delivery = 0,
  orderType,
  printData,
  customerMobile = "",
  reciptDetails,
  onClose,
  isOpen,
}) {
  const { getOrders } = usePosOrders();
  const mobileNumber =
    typeof customerMobile === "object" ? customerMobile.mobile : customerMobile;

  const toNum = (value) => {
    const n = Number(value || 0);
    return Number.isFinite(n) ? n : 0;
  };

  const orderTotalAmount = toNum(
    total ||
      printData?.totalOrderPrice ||
      printData?.due_amount ||
      printData?.pay_amount,
  );

  const actualPaidAmount = toNum(
    paidAmount ||
      printData?.receivedAmount ||
      printData?.received_amount ||
      printData?.pay_amount ||
      orderTotalAmount,
  );

  const dueAmount = toNum(
  printData?.due_amount ||
    printData?.dueAmount ||
    printData?.remaining_amount ||
    printData?.balance_amount ||
    0,
);

const paymentStatus =
  printData?.payment_status ||
  printData?.paymentStatus ||
  "unpaid";

  const formattedPaymentStatus = String(paymentStatus)
  .replace(/_/g, " ")
  .replace(/\b\w/g, (char) => char.toUpperCase());

  const subtotal = toNum(printData?.subtotal || orderTotalAmount);

  const parseCustomizations = (item) => {
    if (Array.isArray(item?.customizations)) return item.customizations;
    if (Array.isArray(item?.customization)) return item.customization;

    try {
      if (
        typeof item?.customization === "string" &&
        item.customization.trim()
      ) {
        return JSON.parse(item.customization);
      }
    } catch (err) {
      console.error("Customization parse error:", err);
    }

    return [];
  };

  const getCustomizationText = (item) => {
    return parseCustomizations(item)
      .flatMap((c) => c?.options || [])
      .filter(Boolean)
      .join(", ");
  };

  const getCustomerAddress = () => {
    const rawAddress =
      printData?.address_line ||
      printData?.address ||
      printData?.customer_address ||
      printData?.billingAddress ||
      printData?.billing_address ||
      printData?.address_json ||
      reciptDetails?.address ||
      "";

    if (!rawAddress) return "";

    if (typeof rawAddress === "string") {
      try {
        const parsed = JSON.parse(rawAddress);

        if (typeof parsed === "string") return parsed;

        return [
          parsed?.address,
          parsed?.addressLine,
          parsed?.address_line,
          parsed?.line1,
          parsed?.line2,
          parsed?.city,
          parsed?.state,
          parsed?.pincode,
          parsed?.pinCode,
        ]
          .filter(Boolean)
          .join(", ");
      } catch {
        return rawAddress;
      }
    }

    if (typeof rawAddress === "object") {
      return [
        rawAddress?.address,
        rawAddress?.addressLine,
        rawAddress?.address_line,
        rawAddress?.line1,
        rawAddress?.line2,
        rawAddress?.city,
        rawAddress?.state,
        rawAddress?.pincode,
        rawAddress?.pinCode,
      ]
        .filter(Boolean)
        .join(", ");
    }

    return "";
  };

  const customerAddress = getCustomerAddress();

  const deliveryChargeAmount = Number(
    delivery ||
      printData?.delivery_fee ||
      printData?.deliveryCharges ||
      printData?.deliveryAmount ||
      printData?.delivery_charge ||
      0,
  );

  const printableCart = cart.map((item) => ({
    ...item,
    customizations: parseCustomizations(item),
  }));

  const receiptItems =
    Array.isArray(printData?.items) && printData.items.length > 0
      ? printData.items.map((item) => ({
          ...item,
          qty: item.qty || item.quantity,
          price: item.price || item.item_price || item.itemPrice,
          customizations: parseCustomizations(item),
        }))
      : printableCart;

  if (!isOpen) return null;

  return (
    <>
  
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 montserrat-font-1"
      // onClick={() => onClose()}
    >
      <div
        className="bg-white rounded-2xl flex overflow-hidden w-full max-w-2xl montserrat-font-1 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Receipt (left) ── */}
        <div className="flex-1 p-8 border-r-2 border-dashed border-gray-200">
          {/* Success icon */}
          <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-7 h-7 text-white"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <h2 className="text-center text-2xl font-semibold text-emerald-600 mb-0.5">
            Order Placed
          </h2>
          <p className="text-center text-sm text-gray-400 mb-6">Successfully</p>

          <p className="text-sm text-gray-500 mb-1">
            Customer Mobile :{" "}
            <span className="text-gray-800 font-medium">{mobileNumber}</span>
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Order Type :{" "}
            <span className="text-gray-800 font-medium">{orderType}</span>
          </p>
          {customerAddress && (
            <p className="text-sm text-gray-500 mb-4 leading-relaxed">
              Address :{" "}
              <span className="text-gray-800 font-medium break-words">
                {customerAddress}
              </span>
            </p>
          )}
          {/* Items table */}
          <table className="w-full text-xs border-collapse mb-4">
            <thead>
              <tr className="bg-gray-50">
                {["#", "Item Name", "Qty", "Unit Price (₹)", "Total (₹)"].map(
                  (h) => (
                    <th
                      key={h}
                      className="border border-gray-200 px-2 py-2 text-left font-medium text-gray-800"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {receiptItems.map((item, i) => {
                const qty = Number(item.qty || item.quantity || 1);

                const price = Number(
                  item.price ||
                    item.item_price ||
                    item.itemPrice ||
                    item.selectedPrice?.price ||
                    0,
                );

                const rowTotal =
                  Number(item.total_price || item.totalPrice || 0) ||
                  price * qty;

                return (
                  <tr key={item.item_id || item.id || i}>
                    <td className="border border-gray-200 px-2 py-2">
                      {i + 1}
                    </td>

                    <td className="border border-gray-200 px-2 py-2">
                      <div className="font-bold text-[14px] text-gray-800">
                        {item.item_name || item.productName || "Item"}
                      </div>

                      {getCustomizationText(item) && (
                        <div className="text-[14px] text-gray-700 mt-1 leading-snug">
                          Custom: {getCustomizationText(item)}
                        </div>
                      )}
                    </td>

                    <td className="border border-gray-200 px-2 py-2 text-center">
                      {qty}
                    </td>

                    <td className="border border-gray-200 px-2 py-2 text-right">
                      ₹{price.toFixed(2)}
                    </td>

                    <td className="border border-gray-200 px-2 py-2 text-right">
                      ₹{rowTotal.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1 montserrat-font-1">
            <div className="flex justify-between text-gray-500">
              <span>Subtotal:</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-gray-500">
              <span>Discount Amount:</span>
              <span>₹{discount}</span>
            </div>
            <div className="flex justify-between text-gray-500">
              <span>Delivery Charges:</span>
              <span>₹ {deliveryChargeAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-semibold text-emerald-600 border-t border-gray-200 pt-2 mt-1">
              <span>Total:</span>
              <span>₹{orderTotalAmount.toFixed(2)}</span>
            </div>

<div className="flex justify-between font-semibold">
  <span>Payment Status:</span>

  <span
    className={`px-2 py-0.5 rounded-full text-xs ${
      paymentStatus === "paid"
        ? "bg-emerald-100 text-emerald-700"
        : paymentStatus === "partially_paid"
          ? "bg-yellow-100 text-yellow-700"
          : "bg-red-100 text-red-600"
    }`}
  >
    {formattedPaymentStatus}
  </span>
</div>

            <div className="flex justify-between font-semibold text-blue-500">
              <span>Pay Amount:</span>
              <span>₹{actualPaidAmount.toFixed(2)}</span>
            </div>

            <div className="flex justify-between font-semibold text-red-500">
              <span>Due Amount:</span>
              <span>₹{dueAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* ── Action buttons (right) ── */}
        <div className="w-44 flex flex-col items-center justify-center  montserrat-font-1 gap-2.5 p-4">
          {["PRINT", "KITCHEN PRINT", "HOME"].map((label) => (
            <button
              key={label}
              onClick={async () => {
                if (label === "PRINT") {

                  if (!printData?.orderid) {
                    console.error("printData not ready");
                    return;
                  }

                  // await processPrintOrder(printData?.orderid);

                  document.getElementById("pos-print-btn")?.click();

                  const t = new Date().toISOString().split("T")[0];
                  await getOrders({ fromDate: t, toDate: t });
                } else if (label === "KITCHEN PRINT") {

                  if (!printData?.orderid) {
                    console.error("Kitchen print data not ready");
                    return;
                  }

                  setTimeout(() => {
                    document.getElementById("pos-kitchen-print-btn")?.click();
                  }, 150);

                  const t = new Date().toISOString().split("T")[0];
                  await getOrders({ fromDate: t, toDate: t });
                } else if (label === "HOME") {
                  onClose("home");
                }
              }}
              className="w-full py-5 rounded-lg border montserrat-font-1 border-gray-200 bg-gray-50 hover:bg-gray-100 mt-5 
      active:scale-95 text-sm font-bold tracking-wide text-gray-900 transition-all"
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "none" }}>
        <POSPrint
          cart={printableCart}
          totalPrice={orderTotalAmount}
          orderType={orderType}
          mobile={customerMobile}
          reciptDetails={reciptDetails}
          apiResponsePrint={printData}
        />
      </div>

      <div style={{ display: "none" }}>
        <PosKOTPrint
          cart={printableCart}
          totalPrice={orderTotalAmount}
          orderType={orderType}
          mobile={customerMobile}
          reciptDetails={reciptDetails}
          apiResponsePrint={printData}
        />
      </div>
    </div>
    </>
  );
}
